import type { Deck, IndexedCard } from "@punkrecords/types";
import { getRulesForDeck } from "./rules.js";

export interface DeckError {
  code: string;
  message: string;
}

export type CardLookup = (cardId: string) => IndexedCard | undefined;

/** Matches official rules text on cards that ignore the usual 4-copy limit. */
const UNLIMITED_DECK_RULE_PHRASE = "any number of this card in your deck";

export function cardAllowsUnlimitedCopies(
  card: IndexedCard | undefined | null,
): boolean {
  const t = card?.text;
  if (!t) return false;
  return t.toLowerCase().includes(UNLIMITED_DECK_RULE_PHRASE);
}

export function validateDeck(
  deck: Deck,
  lookupCard: CardLookup,
): DeckError[] {
  const rules = getRulesForDeck(deck);
  const errors: DeckError[] = [];

  if (!deck.leader) {
    errors.push({ code: "NO_LEADER", message: "Deck must have a leader." });
  }

  const leaderCard = deck.leader ? lookupCard(deck.leader) : undefined;
  if (deck.leader && !leaderCard) {
    errors.push({
      code: "LEADER_NOT_FOUND",
      message: `Leader card "${deck.leader}" not found in library.`,
    });
  }

  if (leaderCard && leaderCard.class.toLowerCase() !== "leader") {
    errors.push({
      code: "LEADER_WRONG_TYPE",
      message: `"${deck.leader}" is not a Leader card.`,
    });
  }

  const leaderColors = new Set(
    (leaderCard?.colors ?? []).map((c: string) => c.toLowerCase()),
  );

  const totalCards = deck.cards.reduce((sum: number, s: { count: number }) => sum + s.count, 0);
  if (totalCards !== rules.mainDeckSize) {
    errors.push({
      code: "WRONG_CARD_COUNT",
      message: `Main deck must have exactly ${rules.mainDeckSize} cards (currently ${totalCards}).`,
    });
  }

  for (const slot of deck.cards) {
    const card = lookupCard(slot.cardId);
    const effectiveMaxCopies = rules.maxCopiesPerCard === -1
      ? rules.mainDeckSize
      : rules.maxCopiesPerCard;
    const maxCopies = cardAllowsUnlimitedCopies(card)
      ? rules.mainDeckSize
      : effectiveMaxCopies;

    if (slot.count < 1 || slot.count > maxCopies) {
      errors.push({
        code: "INVALID_COPY_COUNT",
        message: `"${slot.cardId}" has ${slot.count} copies (must be 1–${maxCopies}).`,
      });
    }

    if (!card) {
      errors.push({
        code: "CARD_NOT_FOUND",
        message: `Card "${slot.cardId}" not found in library.`,
      });
      continue;
    }

    if (card.class.toLowerCase() === "leader") {
      errors.push({
        code: "LEADER_IN_MAIN_DECK",
        message: `"${slot.cardId}" is a Leader and cannot be in the main deck.`,
      });
    }

    if (rules.enforceLeaderColorMatch && leaderColors.size > 0) {
      const cardColors = card.colors.map((c: string) => c.toLowerCase());
      const hasMatchingColor = cardColors.some((c: string) => leaderColors.has(c));
      if (!hasMatchingColor) {
        errors.push({
          code: "COLOR_MISMATCH",
          message: `"${slot.cardId}" (${cardColors.join("/")}) doesn't share a color with the leader (${[...leaderColors].join("/")}).`,
        });
      }
    }
  }

  return errors;
}
